import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { bulkDelete } from 'email-management-lib/EmailArchiveOrDelete/api/delete';
import EmailList from 'email-management-lib/EmailArchiveOrDelete/components/EmailList';
import WorkflowAlert from 'email-management-lib/EmailArchiveOrDelete/components/WorkflowAlert';
import useWorkflows from 'email-management-lib/EmailArchiveOrDelete/hooks/useWorkflows';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { MatchTextArea } from 'foundations-components/transitional/components/MatchTextArea';
import { Modal } from 'foundations-components/transitional/components/Modal';
import Raven from 'raven-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function DeleteModal({
  emails,
  onClose,
  listingLibObserver
}) {
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const {
    emailsWithWFInfo,
    loading: isLoadingWorkflows
  } = useWorkflows({
    emails
  });
  const activeWfsCount = useMemo(() => {
    return emailsWithWFInfo.reduce((acc, email) => {
      return acc + email.flows.length;
    }, 0);
  }, [emailsWithWFInfo]);
  const hasNoActiveWfs = useMemo(() => {
    return emails.every(email => email.isAutomatedLike() && (!email.isPublished() || email.isPublished() && activeWfsCount === 0));
  }, [activeWfsCount, emails]);
  const shouldRenderEmailList = useMemo(() => emails.length > 1, [emails]);
  const maybeRenderSubscriptionEmailWarning = useMemo(() => {
    if (emails.length === 1 && ['OPTIN_EMAIL', 'OPTIN_FOLLOWUP_EMAIL', 'RESUBSCRIBE_EMAIL'].includes(emails[0].emailType)) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `email-management.emailDelete.warnings.subscriptionEmail.${emails[0].emailType}`
      });
    }
  }, [emails]);
  const maybeRenderWorkflowAlert = useMemo(() => {
    if (emails.every(email => !(email.isAutomatedLike() && email.isPublished()))) {
      return null;
    }
    return isLoadingWorkflows ? /*#__PURE__*/_jsx(LoadingSpinner, {}) : /*#__PURE__*/_jsx(WorkflowAlert, {
      emailsWithWFInfo: emailsWithWFInfo,
      action: "delete"
    });
  }, [emails, emailsWithWFInfo, isLoadingWorkflows]);
  const shouldRenderWorkflow = useMemo(() => {
    return emails.some(email => email.isPublished() && email.isAutomatedLike());
  }, [emails]);
  const areSomePublished = useMemo(() => {
    return emails.some(email => email.isPublished());
  }, [emails]);
  useEffect(() => {
    if (areSomePublished) {
      setIsDeleteDisabled(true);
    }
  }, [areSomePublished]);
  const emailIds = useMemo(() => {
    return emails.map(email => email.id);
  }, [emails]);
  const removeFromFrameworkTable = useCallback(() => {
    if (listingLibObserver) {
      listingLibObserver.delete(emails.map(email => email.crmObjectId));
    }
  }, [emails, listingLibObserver]);
  const onDelete = useCallback(() => {
    bulkDelete(emailIds).then(() => {
      removeFromFrameworkTable();
    }).catch(error => {
      Raven.captureException(new Error('Error when deleting emails'), {
        extra: {
          error: error.responseJSON || error
        }
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.errors.email.delete",
          options: {
            count: emailIds.length
          }
        })
      });
    });
    onClose();
  }, [emailIds, onClose, removeFromFrameworkTable]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "danger",
    "data-test-id": "email-delete-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "email-management.emailDelete.title",
          options: {
            count: emails.length
          }
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [hasNoActiveWfs ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "email-management.emailDelete.subtitleNoActiveWfs",
          options: {
            count: emails.length,
            emailName: emails[0].name
          }
        }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "email-management.emailDelete.subtitle",
          options: {
            count: emails.length,
            emailName: emails[0].name
          }
        }), maybeRenderSubscriptionEmailWarning, maybeRenderWorkflowAlert, shouldRenderEmailList && /*#__PURE__*/_jsx(EmailList, {
          isLoadingWorkflows: isLoadingWorkflows,
          emailsWithWFInfo: emailsWithWFInfo,
          shouldRenderWorkflow: shouldRenderWorkflow
        }), areSomePublished && /*#__PURE__*/_jsx(View, {
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(FormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.emailDelete.typeToDelete.label"
            }),
            children: /*#__PURE__*/_jsx(MatchTextArea, {
              match: I18n.text('email-management.emailDelete.typeToDelete.target'),
              onMatchedChange: () => setIsDeleteDisabled(false),
              size: "xxl"
            })
          })
        }), /*#__PURE__*/_jsx(Text, {
          color: "text-primary-subtle",
          marginBlockStart: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailDelete.description"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [/*#__PURE__*/_jsx(Button, {
          "data-test-id": "delete-confirm-button",
          disabled: isDeleteDisabled,
          use: "danger",
          onClick: onDelete,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailDelete.confirm"
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          use: "tertiary-light",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailDelete.cancel"
          })
        })]
      })
    })]
  });
}